'use client';

// Hero CTA row.
//
// Default: "Back our journey" (roadmap) + "Read FAQ".
// Launch landing: direct entry into the network-aware markets app.

import { useTranslations } from 'next-intl';
import type { JSX, MouseEvent } from 'react';

import { MorfiProductButton } from '@/components/support-mainnet/landing-product-link';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Link } from '@/i18n/navigation';
import { scrollToElementId } from '@/lib/ui/scroll-to-id';
import { cn } from '@/lib/ui/utils';

export const SHAKE_DONATE_QR_EVENT = 'morfi:shake-donate-qr' as const;

type HeroCtaRowVariant = 'default' | 'launch';

interface HeroCtaRowProps {
  variant?: HeroCtaRowVariant;
}

function HeroProductCta({ className }: { className?: string }): JSX.Element {
  const tSupport = useTranslations('supportMainnet.hero');

  return (
    <div
      className={cn('flex flex-wrap items-center justify-center gap-3 pt-2 sm:gap-4', className)}
    >
      <MorfiProductButton markerHeroCta surface="hero">
        {tSupport('launchCta')}
      </MorfiProductButton>
    </div>
  );
}

export function HeroCtaRow({ variant = 'default' }: HeroCtaRowProps): JSX.Element {
  const t = useTranslations('supportMainnet.hero');

  if (variant === 'launch') {
    return <HeroProductCta />;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 pt-2 sm:gap-4">
      <AnimatedButton
        variant="primary"
        size="lg"
        onClick={(): void => scrollToElementId('roadmap-heading')}
      >
        {t('ctaDonate')}
      </AnimatedButton>
      <AnimatedButton asChild variant="secondary" size="lg">
        <Link
          href="#faqs"
          onClick={(event: MouseEvent<HTMLAnchorElement>): void => {
            event.preventDefault();
            scrollToElementId('faqs');
            window.history.pushState(null, '', '#faqs'); // ALLOW-NETWORK-UNAWARE: same-page hash sync; does not navigate.
          }}
        >
          {t('ctaFaqs')}
        </Link>
      </AnimatedButton>
    </div>
  );
}
